import React, { memo, useCallback } from 'react';
import { BuildingTooltip } from '@cobu/types';
import { CobuButton, CobuStack, CobuText } from '@cobu/ui';
import { Strings } from '../../constants';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface ChatTooltipProps {
  tooltip: BuildingTooltip;
  onCtaPress: (tooltip: BuildingTooltip) => void;
}

const ChatTooltip: React.FC<ChatTooltipProps> = memo(({ tooltip, onCtaPress }) => {
  const strings = Strings.get();

  const handleCtaPress = useCallback(() => {
    analyticsService.trackEvent(AnalyticsEvent.BUILDING_TOOLTIP_CTA_PRESSED, {
      destination: tooltip.linkType,
      source: 'chat_tab',
      title: tooltip.title,
      tooltipId: tooltip.uuid,
    });

    onCtaPress(tooltip);
  }, [tooltip, onCtaPress]);

  if (!tooltip) return null;

  return (
    <CobuStack
      width="100%"
      bc="$tooltipBannerYellow"
      borderTopWidth={1}
      borderTopColor="$cobuDarkBlue25"
      borderBottomWidth={1}
      borderBottomColor="$cobuDarkBlue25"
      borderRadius={0}
      shadowOpacity={0}
      py="$4"
      px="$3"
      layout="horizontal"
      alignItems="center"
      justifyContent="space-between"
    >
      {/* Left side: Title and subtitle */}
      <CobuStack layout="vertical" gap="$1" flex={1}>
        <CobuText
          fontSize="$4"
          fontWeight="bold"
          color="$black"
        >
          {tooltip.title}
        </CobuText>
        <CobuText
          fontSize="$2"
          color="$colorMuted"
        >
          {tooltip.description}
        </CobuText>
      </CobuStack>

      {/* Right side: CTA Button */}
      <CobuStack ml="$1" mr="$1">
        <CobuButton
          borderRadius="$full"
          backgroundColor="$black"
          borderWidth={0}
          h="$8"
          onPress={handleCtaPress}
          iconRight={(
            <ChevronRightIcon
              width={16}
              height={16}
              style={{ color: 'white' }}
            />
          )}
        >
          <CobuText color="$white" fontSize="$2" fontWeight="bold">
            {tooltip.callToAction || strings.tooltip.defaultCta}
          </CobuText>
        </CobuButton>
      </CobuStack>
    </CobuStack>
  );
});

ChatTooltip.displayName = 'ChatTooltip';

export default ChatTooltip; 